import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import {
  notificationService,
  NotificationDTO,
  renderNotificationText,
} from '../../services/notificationService';
import './NotificationsPage.css';
import { CheckCheck } from 'lucide-react';

const PAGE_SIZE = 20;

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (pageNumber: number, onlyUnread: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await notificationService.getMyNotifications(pageNumber, PAGE_SIZE, onlyUnread);
      setItems(response.data.data?.data ?? []);
      setTotal(response.data.data?.pagination?.total ?? 0);
    } catch {
      setError(t('notifications.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(page, unreadOnly);
  }, [load, page, unreadOnly]);

  const handleItemClick = async (item: NotificationDTO) => {
    if (!item.isRead) {
      try {
        await notificationService.markAsRead(item.id);
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
      } catch {
        // navigation still proceeds
      }
    }
    if (item.linkUrl) navigate(item.linkUrl);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      load(page, unreadOnly);
    } catch {
      setError(t('notifications.failedToLoad'));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const langCode = i18n.language === 'ja' ? 'ja-JP' : 'en-US';
  const formatTime = (iso: string) =>
    new Date(iso).toLocaleString(langCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <Layout>
      <div className="ims-page notifications-page">
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('notifications.title')}</h1>
          <div className="ims-list-actions">
            <button className="ims-btn ims-btn-secondary" onClick={handleMarkAllRead} disabled={loading}>
              <CheckCheck size={15} strokeWidth={2} />
              {t('notifications.markAllRead')}
            </button>
          </div>
        </div>

        <div className="np-filters">
          <button
            type="button"
            className={`np-filter-btn ${!unreadOnly ? 'active' : ''}`}
            onClick={() => { setUnreadOnly(false); setPage(1); }}
          >
            {t('notifications.filterAll')}
          </button>
          <button
            type="button"
            className={`np-filter-btn ${unreadOnly ? 'active' : ''}`}
            onClick={() => { setUnreadOnly(true); setPage(1); }}
          >
            {t('notifications.filterUnread')}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="ims-card">
          <div className="np-list">
            {loading && <div className="np-empty">{t('common.loading')}</div>}
            {!loading && items.length === 0 && (
              <div className="np-empty">{t('notifications.empty')}</div>
            )}
            {!loading &&
              items.map((item) => {
                const text = renderNotificationText(item, t);
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={`np-item ${item.isRead ? '' : 'unread'}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span className={`np-item-dot severity-${item.severity}`} />
                    <span>
                      <p className="np-item-title">{text.title}</p>
                      <p className="np-item-message">{text.message}</p>
                      <span className="np-item-time">{formatTime(item.createdAt)}</span>
                    </span>
                  </button>
                );
              })}
          </div>

          {totalPages > 1 && (
            <div className="np-pager">
              <button
                className="ims-btn ims-btn-secondary"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.previous')}
              </button>
              <span>{t('notifications.pageOf', { page, totalPages })}</span>
              <button
                className="ims-btn ims-btn-secondary"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
